import React from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import IconX from '@/assets/icons/x.svg';
import IconGoogle from '@/assets/icons/google.svg';

export interface LinkedAccountInfo {
  username?: string; // X only
  email?: string; // Google only
  linkedAt: string;
  isPrimary?: boolean;
}

interface LinkedAccountCardProps {
  provider: 'x' | 'google';
  isLinked: boolean;
  accountInfo?: LinkedAccountInfo | null;
  isLinking: boolean;
  onLink: () => void;
  onUnlink: () => void;
  unlinking: boolean;
}

const LinkedAccountCard: React.FC<LinkedAccountCardProps> = ({
  provider,
  isLinked,
  accountInfo,
  isLinking,
  onLink,
  onUnlink,
  unlinking,
}) => {
  const providerName = provider === 'x' ? 'X / Twitter' : 'Google';
  const providerIcon = provider === 'x' ? IconX : IconGoogle;

  return (
    <div className="p-4 border border-gray-700 rounded-lg bg-black/50 hover:border-gray-600 transition-colors">
      <div className="flex items-start justify-between">
        {/* Left: Provider Icon + Info */}
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
            <img 
              src={providerIcon} 
              alt={`${providerName} icon`}
              className="w-5 h-5"
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-white">{providerName}</h3>
              {accountInfo?.isPrimary && (
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-semibold rounded border border-blue-500/40">
                  Primary
                </span>
              )}
            </div>
            
            {isLinked && accountInfo ? (
              <div className="space-y-1">
                <p className="text-sm text-gray-400 truncate">
                  {accountInfo.username 
                    ? `@${accountInfo.username}` 
                    : accountInfo.email}
                </p>
                <p className="text-xs text-gray-500">
                  Linked {formatDistanceToNow(new Date(accountInfo.linkedAt), { addSuffix: true })}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Not linked</p>
            )}
          </div>
        </div>

        {/* Right: Action Button */}
        <div className="ml-3 flex-shrink-0">
          {isLinked ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onUnlink}
              disabled={unlinking || accountInfo?.isPrimary}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed font-['Inter'] font-semibold"
              title={accountInfo?.isPrimary ? 'Cannot unlink primary provider' : 'Unlink this account'}
            >
              {unlinking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Unlinking
                </>
              ) : (
                'Unlink'
              )}
            </Button>
          ) : (
            <Button
              variant="default"
              size="sm"
              onClick={onLink}
              disabled={isLinking}
              className="flex self-stretch justify-center items-center flex-row gap-2 py-3 px-[20px] border-solid border rounded-lg shadow-[inset_0px_0px_8px_0px_rgba(84,49,131,0.6),0px_0px_0px_2px_#0F1D4A] disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[inset_0px_0px_12px_0px_rgba(84,49,131,0.8),0px_0px_0px_2px_#0F1D4A] transition-all text-[#FFFFFF] font-['Inter'] font-semibold"
              style={{
                  background: 'linear-gradient(0deg, #101D4A 0%, #873ED0 100%)',
              }}
            >
              {isLinking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Linking
                </>
              ) : (
                'Link'
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Status Badge */}
      <div className="mt-3 pt-3 border-t border-gray-800">
        {isLinked ? (
          <span className="inline-flex items-center px-2 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded border border-green-500/40">
            <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5"></span>
            Linked
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 bg-gray-500/20 text-gray-400 text-xs font-semibold rounded border border-gray-500/40">
            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-1.5"></span>
            Not linked
          </span>
        )}
      </div>
    </div>
  );
};

export default LinkedAccountCard;
